import React from 'react';

import { mqMin } from '@lucid/theme';

import type { Lucid } from '../../../types/types';
import Box from '../../box/box';
import Text from '../../text/text';
import Link from '../../link/link';
import type { QuoteCtaLinkType, QuoteType } from '../quote';

export interface QuoteCarouselItemType {
  /** Allows the component to get additional styles. */
  className?: string;
  /** List of quotes to include in the carousel */
  quote: QuoteType;
  /** Determines whether or not the quote is currently in view */
  isCurrentQuote?: boolean;
  /** Variant for links in quote */
  linkVariant?: Lucid.Theme.Component.Link['Variant'] | Lucid.Theme.Component.Button['Variant'];
  quoteTextVariant?: Lucid.Theme.Component.Typography;
}

const QuoteCarouselItem = ({
  className,
  quote,
  isCurrentQuote,
  linkVariant,
  quoteTextVariant = 'paragraphXL300',
  ...rest
}: QuoteCarouselItemType) => {
  const quoteLink = quote.cta as QuoteCtaLinkType;
  const attribution = quote.bottomAttribution;
  const linkPath = quoteLink?.url?.path;
  const linkText = quoteLink?.title;
  const quoteText = quote.quote;

  return (
    <Box
      {...rest}
      css={{
        flex: '0 0 100%',
      }}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
    >
      <Box
        css={theme => ({
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          textAlign: 'center',
          maxWidth: '780px',
          margin: 'auto',
          padding: `0 ${theme.space.s48}px`,
          [mqMin.tablet]: {
            padding: `0 ${theme.space.s24}px`,
          },
        })}
      >
        <Text variant={quoteTextVariant}>{quoteText}</Text>
        <Text
          css={theme => ({
            fontWeight: theme.fontWeights.strong,
          })}
          variant="paragraphM400"
        >
          {attribution}
        </Text>
        {linkText && linkPath && (
          <Link
            css={theme => ({
              marginBottom: theme.space.s16,
            })}
            iconAnimation="navigational"
            tabIndex={isCurrentQuote ? 0 : -1}
            to={linkPath}
            variant={linkVariant}
          >
            {linkText}
          </Link>
        )}
      </Box>
    </Box>
  );
};

export default QuoteCarouselItem;
